import {useEffect, useRef, useState} from 'react';
import ClickAwayListener from 'react-click-away-listener';
import {faBars, faMagnifyingGlass} from '@fortawesome/pro-regular-svg-icons';
import isChromatic from 'chromatic/isChromatic';
import {AnimatePresence} from 'motion/react';
import * as motion from 'motion/react-m';
import {useRouter} from 'next/router';
import useTranslation from 'next-translate/useTranslation';
import {twJoin, twMerge} from 'tailwind-merge';

import useDerivedMenuData from '@/components/features/menu/hooks/useDerivedMenuData';
import SkipNav from '@/components/global/layout/SkipNav';
import {useCuisineFiltersRowState} from '@/components/global/providers/CuisineFiltersRowVisibilityProvider';
import {useFulfillmentDetailState} from '@/components/global/providers/FulfillmentDetailStateProvider';
import {useGlobalFulfillmentDetail} from '@/components/global/providers/GlobalFulfillmentDetailProvider';
import {useStoreNextRouter} from '@/components/global/providers/StoreNextRouter';
import {useUserMenuModalContext} from '@/components/global/providers/UserMenuModalProvider';
import EppoFlags from '@/EppoFlags';
import {useBooleanAssignment} from '@/eppoHooks';
import useAppBarLoading from '@/hooks/useAppBarLoading';
import useAuthReturnDestination from '@/hooks/useAuthReturnDestination';
import useIdentity from '@/hooks/useIdentity';
import useOrderingExperience from '@/hooks/useOrderingExperience';
import useTracking from '@/hooks/useTracking';
import {SEARCH_NEW_PATH, SEARCH_ORDER_ID_PATH, SIGN_IN_PATH, SIGN_UP_PATH} from '@/paths';
import {compilePath} from '@/utils';
import {
  JumpBackInHomepageExperimentVariation,
  useJumpBackInHomepage,
} from '../../providers/JumpBackInHomepageProvider/JumpBackInHomepageProvider';
import EventBarV2CollapsedTrigger from './EventBarV2/EventBarV2CollapsedTrigger';
import {useEventBarV2} from './EventBarV2/EventBarV2Provider';
import {useMenuFulfillmentPending} from './EventBarV2/hooks/useMenuFulfillmentPending';
import useEventBarFastFollows from './hooks/useEventBarFastFollows';
import useEventBarRedesign from './hooks/useEventBarRedesign';
import useNavbarVersion from './hooks/useNavbarVersion';
import useNavLinkTracking from './hooks/useNavlinkTracking';
import LoggedOutMenu from './Menus/LoggedOutMenu';
import Admin from './Admin';
import AppBarIconButton from './AppBarIconButton';
import AppBarLink from './AppBarLink';
import {NAVLINK_TRACKING} from './appBarTracking';
import Cart from './Cart';
import EventBar from './EventBar';
import EventBarV2 from './EventBarV2';
import Help from './Help';
import Logo from './Logo';
import {MenuModal} from './Menus';
import Notifications from './Notifications';
import Orders from './Orders';
import {SearchBar} from './SearchBar';
import {User} from './User';

const AppBar: React.FC<{
  alwaysShowEventBarV2?: boolean;
  isSearchPageEventBarVisible?: boolean;
  isCollapsibleSearchBar?: boolean;
}> = ({
  alwaysShowEventBarV2 = false,
  isSearchPageEventBarVisible = false,
  isCollapsibleSearchBar = false,
}) => {
  const [isSearchBarOpen, setIsSearchBarOpen] = useState(false);
  const [isEventDetailsExpanded, setIsEventDetailsExpanded] = useState(false);
  const [isCollapsePending, setIsCollapsePending] = useState(false);
  const {fulfillmentDetailState, FulfillmentDetailStates} = useFulfillmentDetailState();
  const {openCommand} = useEventBarV2();
  const pending = useMenuFulfillmentPending();
  const {isUserMenuModalOpen, setUserMenuModalOpen} = useUserMenuModalContext();
  const {showAppBarLoading} = useAppBarLoading();
  const {isAdmin, isLoggedIn} = useIdentity();
  const router = useRouter();
  const {
    isOnBrandListPage,
    isOnCuisineMetroPage,
    isOnContactUsPage,
    isOnLoggedOutLandingPage,
    isOnMetroPage,
    isOnSearchPage,
  } = useStoreNextRouter();
  const {fulfillmentDetail} = useGlobalFulfillmentDetail();
  const {t} = useTranslation('app-bar');
  const {track} = useTracking();
  const {caterer} = useDerivedMenuData();
  const {isEater, isEzOrdering} = useOrderingExperience();
  // ezO customers order from one restaurant, so there's no event bar or search to show them.
  const hidesEventBarAndSearch = isEater || isEzOrdering;
  const {isVisible: isCuisineFiltersRowVisible} = useCuisineFiltersRowState();
  const disableDirectConnect = useBooleanAssignment(EppoFlags.DisableDirectConnect);
  const {returnParams} = useAuthReturnDestination();

  const {trackRumAction} = useNavLinkTracking();
  const profileVersion = useNavbarVersion();
  const {
    showEventBarRedesign: isEventBarRedesignFlagOn,
    seenRef,
    inView: eventBarSeen,
    isAppBarXL,
  } = useEventBarRedesign({
    trackExposure: !alwaysShowEventBarV2,
  });
  const showEventBarRedesign = isEventBarRedesignFlagOn || alwaysShowEventBarV2;
  // FDIC + EventBarV2: collapse to a search pill at xl. Don't key this on isAppBarXL or V2
  // remounts when the media query resolves.
  const showV2CollapsibleSearchBar =
    isCollapsibleSearchBar && showEventBarRedesign && !hidesEventBarAndSearch;

  // The collapsed FDIC trigger carries the treatment pill from appbar-lg, so that path reports from lg.
  useEventBarFastFollows({
    minAppBarViewport: showV2CollapsibleSearchBar ? 'lg' : 'xl',
    trackExposure: !hidesEventBarAndSearch && showEventBarRedesign && eventBarSeen,
  });

  const {
    variation: jumpBackInHomepageVariation,
    loading,
    hasFulfillmentDetails,
  } = useJumpBackInHomepage();

  const shouldShowJumpBackInHomepage =
    jumpBackInHomepageVariation === JumpBackInHomepageExperimentVariation.TREATMENT &&
    !loading &&
    hasFulfillmentDetails;
  const isControlLandingPage = isOnLoggedOutLandingPage && !shouldShowJumpBackInHomepage;

  const lastOpenCommandNonceRef = useRef(openCommand?.nonce ?? 0);

  // Finish collapsing once the event bar goes idle (a click on the dimmed page background requested
  // it via isCollapsePending). Safe to setState here: it reacts to external fulfillment state and
  // clears isCollapsePending, so it runs once and can't loop.
  useEffect(() => {
    if (!isCollapsePending) return;
    if (showV2CollapsibleSearchBar) {
      // Wait for the V2 catch-all (or a field Update) to clear staged edits. The overlay
      // instance enables catch-all from appbar-lg, matching where this pill is on-screen.
      if (pending.hasChanges) return;
    } else {
      const {NONE, COACHMARK} = FulfillmentDetailStates;
      if (fulfillmentDetailState !== NONE && fulfillmentDetailState !== COACHMARK) return;
    }
    // eslint-disable-next-line react-hooks/set-state-in-effect -- syncing collapse to the external event-bar confirmation state
    setIsEventDetailsExpanded(false);
    setIsSearchBarOpen(false);
    setIsCollapsePending(false);
  }, [
    FulfillmentDetailStates,
    fulfillmentDetailState,
    isCollapsePending,
    pending.hasChanges,
    showV2CollapsibleSearchBar,
  ]);

  useEffect(() => {
    if (!isEventDetailsExpanded) return undefined;
    const {NONE, COACHMARK} = FulfillmentDetailStates;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      if (showV2CollapsibleSearchBar) {
        setIsCollapsePending(true);
        return;
      }
      if (fulfillmentDetailState === NONE || fulfillmentDetailState === COACHMARK) {
        setIsCollapsePending(true);
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [
    FulfillmentDetailStates,
    fulfillmentDetailState,
    isEventDetailsExpanded,
    showV2CollapsibleSearchBar,
  ]);

  // Cart/banner CTAs call openEventBarField while the FDIC bar is a collapsed search pill.
  useEffect(() => {
    if (!openCommand || !isCollapsibleSearchBar) return;
    if (openCommand.nonce === lastOpenCommandNonceRef.current) return;
    lastOpenCommandNonceRef.current = openCommand.nonce;
    setIsCollapsePending(false);
    setIsEventDetailsExpanded(true);
  }, [isCollapsibleSearchBar, openCommand]);

  const showEventBarArea =
    !hidesEventBarAndSearch && !showV2CollapsibleSearchBar && !(showEventBarRedesign && isAppBarXL);
  const showCollapsibleSearchBar = isCollapsibleSearchBar && !hidesEventBarAndSearch;

  const expandEventDetails = () => {
    track('event-bar-expanded', {caterer_id: caterer?.id ?? null, page: window.location.pathname});
    setIsCollapsePending(false);
    setIsEventDetailsExpanded(true);
  };

  const onBackdropClickAway = (event: FocusEvent | MouseEvent | TouchEvent) => {
    if (!(event.target as HTMLElement | null)?.closest('[data-testid="app-bar-backdrop"]')) return;
    setIsCollapsePending(true);
  };

  const slideIn = {
    delay: isChromatic() ? 0 : 0.12,
    duration: isChromatic() ? 0 : 0.2,
  };
  const horizontalSwap = 8;

  // ezx560::Search::EventBarRedesign exposure anchor: mounted identically in both arms wherever an
  // event-bar area exists, so `inView` can't depend on the arm. Absolute + 1px to stay out of flow.
  // Exposure reporting itself is suppressed via trackExposure when alwaysShowEventBarV2 is set
  // (e.g. /home) - that page isn't part of this experiment (every arm sees the same UI there), so
  // it shouldn't count toward its exposure.
  const eventBarExposureSentinel = (
    <div
      aria-hidden
      className="pointer-events-none absolute left-0 top-0 size-px"
      data-testid="event-bar-exposure-sentinel"
      ref={seenRef}
    />
  );

  const collapsibleSearchBar = (
    <ClickAwayListener onClickAway={onBackdropClickAway}>
      <div
        className={twJoin(
          'hidden w-full flex-1 items-center appbar-lg:flex',
          showEventBarRedesign && 'appbar-xl:hidden',
        )}
      >
        {isEventDetailsExpanded ? (
          <motion.div
            animate={{opacity: 1, y: 0}}
            className="absolute bottom-0 left-[200px] flex items-center gap-4 pb-4"
            initial={{opacity: 0, y: 8}}
            transition={slideIn}
          >
            <div className="hidden appbar-lg:block">
              <EventBar onUpdateApplied={() => setIsCollapsePending(true)} />
            </div>
            <div className="w-[330px]">
              <SearchBar
                anchorDropdownInPlace
                fullWidthWhenClosed
                isOpen={isSearchBarOpen}
                setIsOpen={setIsSearchBarOpen}
              />
            </div>
          </motion.div>
        ) : (
          <div className="relative w-full max-w-[773px]">
            <button
              aria-label={t('search.input.expandEventDetails')}
              className="absolute inset-0 z-1 rounded-full"
              data-testid="simplified-search-expand-trigger"
              onClick={expandEventDetails}
              type="button"
            />
            <SearchBar
              fullWidthWhenClosed
              isOpen={isSearchBarOpen}
              setIsOpen={setIsSearchBarOpen}
            />
          </div>
        )}
      </div>
    </ClickAwayListener>
  );

  const collapsibleEventBarV2 = (
    <ClickAwayListener onClickAway={onBackdropClickAway}>
      {/* Visible whenever this instance is mounted (menu FDIC+V2 only). Hide below appbar-lg
          with max-* so the default is displayed — `hidden` + min-width unhide stays
          display:none when that query does not match. min-w-0 lets the pill shrink with the
          navbar instead of holding EventBarV2's max-content width. */}
      <div className="relative min-w-0 max-appbar-lg:hidden">
        <motion.div
          animate={isEventDetailsExpanded ? {opacity: 1, x: 0} : {opacity: 0, x: horizontalSwap}}
          className={twJoin(
            'min-w-0 max-w-full',
            isEventDetailsExpanded ? undefined : 'pointer-events-none',
          )}
          initial={false}
          inert={isEventDetailsExpanded ? undefined : true}
          transition={slideIn}
        >
          <EventBarV2
            className="min-w-0 max-w-full"
            enableMenuCatchAll
            isCompressed
            isVisible={isEventDetailsExpanded}
            onUpdateApplied={() => setIsCollapsePending(true)}
          />
        </motion.div>
        <AnimatePresence initial={false}>
          {!isEventDetailsExpanded && (
            <motion.div
              animate={{opacity: 1, x: 0}}
              className="absolute inset-0"
              exit={{opacity: 0, x: -horizontalSwap}}
              initial={false}
              key="event-bar-v2-collapsed"
              transition={slideIn}
            >
              <EventBarV2CollapsedTrigger onExpand={expandEventDetails} />
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </ClickAwayListener>
  );

  const legacyEventBar = (
    <div
      className={twJoin(
        'relative w-full items-center gap-4',
        showEventBarRedesign ? 'hidden appbar-lg:flex appbar-xl:hidden' : 'hidden appbar-lg:flex',
      )}
    >
      {/* Fade out the EventBar when the search bar opens to reduce clutter. */}
      <div className="hidden appbar-lg:block">
        <motion.div
          animate={isSearchBarOpen ? 'hidden' : 'visible'}
          initial={false}
          variants={{hidden: {opacity: 0}, visible: {opacity: 1}}}
        >
          <EventBar />
        </motion.div>
      </div>

      <div className="hidden flex-1 appbar-xl:block">
        <SearchBar isOpen={isSearchBarOpen} setIsOpen={setIsSearchBarOpen} />
      </div>
    </div>
  );

  return (
    <>
      <SkipNav />

      <div>
        <AnimatePresence>
          {isEventDetailsExpanded && (
            <motion.div
              animate={{opacity: 1}}
              aria-hidden
              className="fixed inset-0 z-1200 bg-black/40"
              data-testid="app-bar-backdrop"
              exit={{opacity: 0}}
              initial={{opacity: 0}}
              transition={{duration: isChromatic() ? 0 : 0.2}}
            />
          )}
        </AnimatePresence>

        <div
          className={twMerge(
            'relative flex h-20 items-center justify-between overflow-hidden bg-white px-2 transition-[height] duration-300 ease-out motion-reduce:transition-none appbar-sm:px-3 appbar-lg:gap-6 appbar-lg:px-6',
            isCuisineFiltersRowVisible && 'border-b',
            isOnLoggedOutLandingPage && 'border-none bg-green-700 text-white',
            isCollapsibleSearchBar && 'items-start',
            isEventDetailsExpanded && 'z-1300 overflow-visible',
            // V1 stacked EventBar+SearchBar needs extra height. V2 overlay is a single compressed
            // row from appbar-lg, so keep the default h-20 instead of growing an empty block.
            isEventDetailsExpanded &&
              showCollapsibleSearchBar &&
              !showV2CollapsibleSearchBar &&
              'h-[162px]',
          )}
          data-testid="app-bar"
        >
          {/* --- Overlay to darken the mobile/tablet background when on the home page --- */}
          <div
            className={twMerge(
              'pointer-events-none absolute inset-0 hidden bg-black/30',
              isOnLoggedOutLandingPage && 'bg-green-700 max-desktop924:block',
            )}
          />

          {/* --- Left side --- */}
          <div
            className={twJoin(
              'flex flex-1 items-center gap-1 appbar-lg:gap-6',
              isCollapsibleSearchBar && 'h-20',
              showV2CollapsibleSearchBar && 'min-w-0',
            )}
            data-testid="app-bar-left-side"
          >
            <AppBarIconButton
              aria-label={t('menus.label')}
              className={twJoin(
                isOnLoggedOutLandingPage
                  ? shouldShowJumpBackInHomepage
                    ? 'block appbar-md:hidden'
                    : 'hidden appbar-md:block appbar-lg:hidden'
                  : 'appbar-lg:hidden',
              )}
              data-testid="app-bar-mobile-menu-button"
              icon={faBars}
              onClick={() => {
                trackRumAction({
                  datadogAction: NAVLINK_TRACKING.profile.clicked,
                  version: profileVersion,
                });
                setUserMenuModalOpen(!isUserMenuModalOpen);
              }}
              onKeyDown={(event: React.KeyboardEvent) =>
                event.key === 'Escape' && setUserMenuModalOpen(false)
              }
              variant={isOnLoggedOutLandingPage ? 'contrast' : 'secondary'}
            />

            {isUserMenuModalOpen && <MenuModal onDismiss={() => setUserMenuModalOpen(false)} />}

            <div
              className={twJoin(
                'flex items-center gap-6',
                isCollapsibleSearchBar && !showV2CollapsibleSearchBar && 'flex-1',
                showV2CollapsibleSearchBar && 'min-w-0 flex-1',
              )}
            >
              <Logo />

              {isOnLoggedOutLandingPage ||
              isOnCuisineMetroPage ||
              isOnMetroPage ||
              isOnBrandListPage ? (
                <div className="hidden appbar-lg:block">
                  <LoggedOutMenu
                    isOnCuisineMetroPage={isOnCuisineMetroPage}
                    isOnMetroPage={isOnMetroPage}
                  />
                </div>
              ) : (
                <>
                  {!hidesEventBarAndSearch && eventBarExposureSentinel}
                  {showEventBarArea &&
                    (showCollapsibleSearchBar ? collapsibleSearchBar : legacyEventBar)}
                  {!hidesEventBarAndSearch &&
                    showEventBarRedesign &&
                    (showV2CollapsibleSearchBar ? (
                      collapsibleEventBarV2
                    ) : (
                      <div className="hidden appbar-xl:block">
                        <AnimatePresence initial={false}>
                          {!isSearchPageEventBarVisible && (
                            <motion.div
                              animate={{opacity: 1}}
                              exit={{opacity: 0}}
                              initial={{opacity: 0}}
                              transition={{duration: isChromatic() ? 0 : 0.3}}
                            >
                              <EventBarV2 isCompressed isVisible={!isSearchPageEventBarVisible} />
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    ))}
                </>
              )}
            </div>
          </div>

          {/* --- Right side --- */}
          <div
            className={twMerge(
              'flex items-center gap-2 appbar-xl:gap-3',
              // Keep the nav row at h-20 (its collapsed position) while the bar grows below it.
              isCollapsibleSearchBar && 'h-20',
              showV2CollapsibleSearchBar && 'shrink-0',
            )}
            data-testid="app-bar-right-side"
          >
            {!isEzOrdering && (
              <AppBarIconButton
                aria-label={t('search.input.label')}
                className={twJoin(
                  isOnLoggedOutLandingPage
                    ? 'hidden appbar-md:block appbar-xl:hidden'
                    : 'appbar-xl:hidden',
                )}
                data-testid="app-bar-search-icon-button"
                icon={faMagnifyingGlass}
                onClick={() => {
                  if (isOnSearchPage) {
                    return document?.getElementById('app-bar-keyword-search-input')?.focus();
                  }
                  if (!isOnSearchPage && fulfillmentDetail?.id) {
                    return router.push(
                      compilePath(SEARCH_ORDER_ID_PATH, {orderId: fulfillmentDetail.id}),
                    );
                  }
                  return router.push(compilePath(SEARCH_NEW_PATH));
                }}
                title={t('search.input.label')}
                variant={isOnLoggedOutLandingPage ? 'contrast' : 'secondary'}
              />
            )}

            {isOnLoggedOutLandingPage && (
              <div className="hidden appbar-xl:block">
                <AppBarLink
                  label={t('navLinks.supportPhoneNumber')}
                  href={t('navLinks.supportPhoneNumberTel')}
                  variant="contrastButton"
                />
              </div>
            )}

            <div className="contents appbar-xl:flex appbar-xl:items-center appbar-xl:gap-1">
              {!isOnContactUsPage && <Help />}
              <Orders />
            </div>

            <div className="flex items-center gap-2 appbar-xl:gap-3">
              {isLoggedIn ? (
                <User />
              ) : (
                <>
                  <div className={twJoin(!isControlLandingPage && 'hidden appbar-md:block')}>
                    <AppBarLink
                      label={t('navLinks.signIn')}
                      path={SIGN_IN_PATH}
                      queryParams={returnParams}
                      variant={isOnLoggedOutLandingPage ? 'contrastButton' : 'secondaryButton'}
                    />
                  </div>
                  <div className={twJoin(!isOnLoggedOutLandingPage && 'hidden appbar-sm:block')}>
                    <AppBarLink
                      label={t('navLinks.signUp')}
                      path={SIGN_UP_PATH}
                      queryParams={returnParams}
                      variant={isOnLoggedOutLandingPage ? 'secondaryButton' : 'button'}
                    />
                  </div>
                </>
              )}

              {isLoggedIn && !disableDirectConnect && <Notifications />}

              {(shouldShowJumpBackInHomepage || !isOnLoggedOutLandingPage) && <Cart />}
              {isAdmin && <Admin />}
            </div>
          </div>
        </div>

        {showAppBarLoading && (
          <div className="h-1 w-full overflow-hidden bg-ez-green-light">
            <div className="h-full w-1/3 animate-[slide_1.2s_linear_infinite] bg-gradient-to-r from-basil-green to-ez-green" />
          </div>
        )}
      </div>
    </>
  );
};

export default AppBar;
